// Support chat state shared by the /app/support page and the assistant dock (SUP-01 / DOCK-01): my conversations,
// the open conversation, sending (optimistic), "talk to a person", resolve, and a 4 s poll for team replies that
// runs only while `active` (page shown / dock open on the Support tab) and the browser tab is visible.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listMyConversations, startConversation, getConversation, sendMessage, requestHuman, closeConversation,
  rateConversation, getStatusNote,
} from './supportApi';

export const SUPPORT_POLL_MS = 4000;

export const SUPPORT_BANNERS = {
  HUMAN_REQUESTED: 'A member of the support team has been notified and will reply here. You can keep adding details.',
  HUMAN_ACTIVE: "You're chatting with a person from our support team.",
  RESOLVED: 'This conversation is resolved. Write again if you need more help.',
  CLOSED: 'This conversation is closed. Start a new one if you need more help.',
};

/** The banner over the chat: who is with the customer, or that the team is offline (they're emailed the reply). */
export function supportBanner(conv) {
  if (!conv) return null;
  const name = conv.agent && conv.agent.name;
  if (conv.state === 'HUMAN_REQUESTED') {
    // Signed-in customers: the reply also goes to their account email when they're not in the chat.
    if (conv.team_online === false) {
      return 'Our team is offline right now. We’ll answer here as soon as we’re back and email you the reply, so you can close this.';
    }
    if (name) return `${name} from our support team is looking at your conversation.`;
  }
  if (conv.state === 'HUMAN_ACTIVE' && name) return `You're chatting with ${name} from our support team.`;
  return SUPPORT_BANNERS[conv.state] || null;
}

/** "Maya" while a team member is typing a reply, else null. */
export const agentTyping = (conv) => (conv && conv.agent && conv.agent.typing ? conv.agent.name || 'Support' : null);

const mergeMessages = (prev, next) => {
  const seen = new Set(prev.map((m) => m.id));
  return prev.concat((next || []).filter((m) => !seen.has(m.id)));
};
const lastId = (c) => (c && c.messages && c.messages.length ? c.messages[c.messages.length - 1].id : undefined);

export default function useSupportChat(active = true) {
  const [list, setList] = useState(null);
  const [conv, setConv] = useState(null);       // { id, state, subject, messages }
  const [pending, setPending] = useState(null); // optimistic customer text while the assistant answers
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const convRef = useRef(null);
  convRef.current = conv;
  const busyRef = useRef(false);
  busyRef.current = busy;
  const started = useRef(false);
  const [statusNote, setStatusNote] = useState(null);   // known problem right now (status_note.py)

  const refreshList = useCallback(() => listMyConversations()
    .then((d) => {
      const rows = Array.isArray(d && d.conversations) ? d.conversations : [];
      setList(rows);
      return rows;
    })
    .catch((e) => { setList([]); setError(e.message); return []; }), []);

  const open = useCallback((id) => {
    setError('');
    return getConversation(id).then((d) => setConv(d && Array.isArray(d.messages) ? d : null))
      .catch((e) => setError(e.message));
  }, []);

  // first activation: resume the open conversation, if any
  useEffect(() => {
    if (!active || started.current) return;
    started.current = true;
    getStatusNote().then(setStatusNote);
    refreshList().then((rows) => {
      const current = rows.find((c) => c.state !== 'CLOSED' && c.state !== 'RESOLVED');
      if (current && !convRef.current) open(current.id);
    });
  }, [active, refreshList, open]);

  const id = conv && conv.id;
  const state = conv && conv.state;
  useEffect(() => {
    if (!active || !id || state === 'CLOSED') return undefined;
    const t = setInterval(() => {
      const c = convRef.current;
      if (!c || document.hidden || busyRef.current) return;
      getConversation(c.id, lastId(c)).then((d) => {
        if (!d || !Array.isArray(d.messages)) return;
        if (d.state !== c.state) refreshList();
        setConv((prev) => (prev && prev.id === d.id
          ? { ...prev, ...d, messages: mergeMessages(prev.messages, d.messages) } : prev));
      }).catch(() => {});
    }, SUPPORT_POLL_MS);
    return () => clearInterval(t);
  }, [active, id, state, refreshList]);

  /** Sends `text`; resolves true on success, false on error (the caller keeps the draft). */
  const send = useCallback(async (text) => {
    const content = (text || '').trim();
    if (!content || busyRef.current) return false;
    setBusy(true); setError(''); setPending(content);
    try {
      const c = convRef.current;
      if (!c || c.state === 'CLOSED') {
        const d = await startConversation(content);
        setConv(d);
        refreshList();
      } else {
        const d = await sendMessage(c.id, content, lastId(c));
        if (d.state !== c.state) refreshList();
        setConv((prev) => ({ ...prev, ...d, messages: mergeMessages(prev.messages, d.messages) }));
      }
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setPending(null); setBusy(false);
    }
  }, [refreshList]);

  const act = useCallback(async (fn) => {
    const c = convRef.current;
    if (!c || busyRef.current) return;
    setBusy(true); setError('');
    try {
      setConv(await fn(c.id));
      refreshList();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, [refreshList]);

  const newConversation = useCallback(() => { setConv(null); setError(''); }, []);

  /** 👍 (1) / 👎 (-1) for the open conversation. */
  const rate = useCallback((value) => {
    const c = convRef.current;
    if (!c) return;
    setConv((prev) => (prev && prev.id === c.id ? { ...prev, rating: value } : prev));
    rateConversation(c.id, value).catch((e) => setError(e.message));
  }, []);

  return {
    list, conv, pending, busy, error, state, open, refreshList, send, newConversation, rate,
    statusNote: conv && 'status_note' in conv ? conv.status_note : statusNote,
    canRate: !!conv && (state === 'RESOLVED' || state === 'CLOSED') && conv.messages.some((m) => m.sender_type === 'ai' || m.sender_type === 'human'),
    requestHuman: () => act(requestHuman),
    resolve: () => act(closeConversation),
    canAskHuman: !!conv && (state === 'AI_ACTIVE' || state === 'RESOLVED'),
    canResolve: !!conv && state !== 'CLOSED' && state !== 'RESOLVED',
  };
}
